import mqtt, { type MqttClient, type IClientOptions } from 'mqtt'
import type { MqttConnector } from '@/types/auth'
import { buildMqttWsUrl } from './url'

export type MqttMessageHandler = (topic: string, payload: Buffer) => void
export type MqttConnectionHandler = () => void
export type MqttErrorHandler = (error: Error) => void

export interface MqttClientHandlers {
  onConnect?: MqttConnectionHandler
  onReconnect?: MqttConnectionHandler
  onClose?: MqttConnectionHandler
  onError?: MqttErrorHandler
  onMessage?: MqttMessageHandler
}

class MqttClientManager {
  private client: MqttClient | null = null
  private connector: MqttConnector | null = null
  private handlers: MqttClientHandlers = {}
  private onReconnectCallback: (() => void) | null = null

  setHandlers(handlers: MqttClientHandlers): void {
    this.handlers = { ...this.handlers, ...handlers }
  }

  setOnReconnect(callback: () => void): void {
    this.onReconnectCallback = callback
  }

  getConnector(): MqttConnector | null {
    return this.connector
  }

  isConnected(): boolean {
    return this.client?.connected ?? false
  }

  getClient(): MqttClient | null {
    return this.client
  }

  async connect(connector: MqttConnector): Promise<void> {
    if (typeof window === 'undefined') return

    this.connector = connector

    if (this.client) {
      this.disconnect()
    }

    const url = buildMqttWsUrl(connector)
    const options: IClientOptions = {
      username: connector.userName,
      password: connector.password,
      clientId: connector.clientId,
      reconnectPeriod: 5000,
      connectTimeout: 30_000,
      clean: true,
      keepalive: 60
    }

    return new Promise((resolve, reject) => {
      const client = mqtt.connect(url, options)
      this.client = client
      let settled = false

      const onConnect = () => {
        console.log('[MQTT] 已连接:', url)
        this.handlers.onConnect?.()
        if (settled) {
          this.onReconnectCallback?.()
          return
        }
        settled = true
        resolve()
      }

      const onError = (error: Error) => {
        console.error('[MQTT] 连接错误:', error)
        this.handlers.onError?.(error)
        if (!client.connected && !settled) {
          settled = true
          reject(error)
        }
      }

      client.on('connect', onConnect)
      client.once('error', onError)

      client.on('reconnect', () => {
        console.log('[MQTT] 重连中...')
        this.handlers.onReconnect?.()
      })

      client.on('close', () => {
        console.log('[MQTT] 连接已关闭')
        this.handlers.onClose?.()
      })

      client.on('message', (topic, payload) => {
        this.handlers.onMessage?.(topic, payload)
      })
    })
  }

  subscribe(topic: string | string[], qos: 0 | 1 | 2 = 0): void {
    this.client?.subscribe(topic, { qos }, (err) => {
      if (err) {
        console.error('[MQTT] 订阅失败:', topic, err)
      }
    })
  }

  unsubscribe(topic: string | string[]): void {
    this.client?.unsubscribe(topic)
  }

  publish(topic: string, message: string | Buffer, qos: 0 | 1 | 2 = 0): void {
    this.client?.publish(topic, message, { qos })
  }

  disconnect(): void {
    if (!this.client) return

    this.client.end(true)
    this.client = null
    this.connector = null
    this.onReconnectCallback = null
  }

  /** 使用已保存的 connector 参数重连 */
  async reconnectFromStorage(getConnector: () => MqttConnector | null): Promise<void> {
    const connector = getConnector()
    if (!connector) return
    await this.connect(connector)
  }
}

export const mqttClient = new MqttClientManager()
export default mqttClient
